import React, { useEffect, useState } from "react";
import { FileText, Download, Send, Loader2 } from "lucide-react";
import api, { API } from "../../lib/api";
import { toast } from "sonner";

/**
 * TaxLettersPanel — admin year-end tax-letter batch generator.
 *
 * One click: groups all paid donations in the chosen year by donor,
 * builds an IRS-style tax-substantiation PDF for each donor,
 * optionally bulk-emails each donor their letter,
 * downloads the full ZIP for the board's records, and
 * appends a row to the audit log (tax_letter_batches).
 */
const currentYear = new Date().getFullYear();

const TaxLettersPanel = () => {
    const [year, setYear] = useState(currentYear - 1); // previous tax year by default
    const [sendEmails, setSendEmails] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [batches, setBatches] = useState([]);
    const [loadingBatches, setLoadingBatches] = useState(true);

    const loadBatches = () =>
        api.get("/admin/tax-letters")
            .then((r) => setBatches(r.data || []))
            .catch(() => {})
            .finally(() => setLoadingBatches(false));

    useEffect(() => { loadBatches(); }, []);

    const generate = async () => {
        setGenerating(true);
        try {
            // Use fetch so we can stream the binary ZIP response directly to the user
            const headers = {};
            const token = localStorage.getItem("rdf_token");
            if (token) headers["Authorization"] = `Bearer ${token}`;
            const resp = await fetch(`${API}/admin/tax-letters/${year}?send_emails=${sendEmails}`, {
                method: "POST",
                credentials: "include",
                headers,
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({ detail: `Generate failed (HTTP ${resp.status})` }));
                toast.error(err.detail || "Generate failed");
                return;
            }
            const donorCount = resp.headers.get("X-Donor-Count");
            const sentCount = resp.headers.get("X-Emails-Sent");
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `reddog-tax-letters-${year}.zip`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success(
                sendEmails
                    ? `Generated ${donorCount} letters and emailed ${sentCount} donors`
                    : `Generated ${donorCount} letters (no emails sent)`
            );
            await loadBatches();
        } catch (err) {
            toast.error(err.message || "Generate failed");
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div data-testid="admin-tax-letters-panel">
            <div className="bg-white border border-black/5 rounded-3xl p-8 mb-8">
                <div className="flex items-start gap-3 mb-6">
                    <FileText className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <div>
                        <h2 className="font-heading text-2xl text-[#1C252E] font-medium">Year-end tax letter batch</h2>
                        <p className="text-sm text-[#4A5568] mt-1.5 max-w-2xl">
                            One click: groups every paid donation in the chosen year by donor, builds an IRS-style tax-substantiation PDF for each donor, optionally bulk-emails each donor their letter, and downloads the full ZIP for board records. Letters include the standard "no goods or services were provided" statement.
                        </p>
                    </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-4 mb-6">
                    <div>
                        <label htmlFor="tax-year-input" className="text-xs uppercase tracking-wider font-bold text-[#4A5568] block mb-2">Tax year</label>
                        <input
                            id="tax-year-input"
                            type="number"
                            min={2024}
                            max={currentYear}
                            value={year}
                            onChange={(e) => setYear(parseInt(e.target.value, 10) || currentYear - 1)}
                            data-testid="admin-tax-letters-year-input"
                            className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
                        />
                    </div>
                    <label className="flex items-start gap-2 cursor-pointer pt-7" data-testid="admin-tax-letters-emails-label">
                        <input
                            type="checkbox"
                            checked={sendEmails}
                            onChange={(e) => setSendEmails(e.target.checked)}
                            data-testid="admin-tax-letters-emails-checkbox"
                            className="mt-1 w-4 h-4 accent-[#E53935]"
                        />
                        <span className="text-sm text-[#4A5568] leading-relaxed">
                            <span className="font-medium text-[#1C252E]">Bulk-email donors</span><br />
                            <span className="text-xs">Each donor receives their letter PDF + Foundation EIN letter + Articles of Incorporation, so their CPA has everything in one email.</span>
                        </span>
                    </label>
                    <div className="pt-7">
                        <button
                            onClick={generate}
                            disabled={generating}
                            data-testid="admin-tax-letters-generate-btn"
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] disabled:bg-[#4A5568] disabled:cursor-not-allowed rounded-full px-6 py-3 font-medium transition-all"
                        >
                            {generating ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                                    Generating…
                                </>
                            ) : (
                                <>
                                    {sendEmails ? <Send className="w-4 h-4" strokeWidth={1.5} /> : <Download className="w-4 h-4" strokeWidth={1.5} />}
                                    Generate &amp; download {year}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Audit log */}
            <div className="bg-white border border-black/5 rounded-3xl p-8" data-testid="admin-tax-letters-audit">
                <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-4">Generation history</h3>
                {loadingBatches ? (
                    <p className="text-[#4A5568] text-sm">Loading…</p>
                ) : batches.length === 0 ? (
                    <p className="text-[#4A5568] text-sm" data-testid="admin-tax-letters-empty">
                        No batches generated yet. Run your first one when the year closes.
                    </p>
                ) : (
                    <div className="overflow-x-auto" data-testid="admin-tax-letters-history">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-black/10 text-left text-xs uppercase tracking-wider text-[#4A5568]">
                                    <th className="py-3 pr-4">Year</th>
                                    <th className="py-3 pr-4">Donors</th>
                                    <th className="py-3 pr-4">Donations</th>
                                    <th className="py-3 pr-4">Total raised</th>
                                    <th className="py-3 pr-4">Emails sent</th>
                                    <th className="py-3 pr-4">Generated by</th>
                                    <th className="py-3">When</th>
                                </tr>
                            </thead>
                            <tbody>
                                {batches.map((b, i) => (
                                    <tr key={i} className="border-b border-black/5" data-testid={`admin-tax-letters-row-${i}`}>
                                        <td className="py-3 pr-4 font-medium text-[#1C252E]">{b.year}</td>
                                        <td className="py-3 pr-4 text-[#4A5568]">{b.donor_count}</td>
                                        <td className="py-3 pr-4 text-[#4A5568]">{b.donation_count}</td>
                                        <td className="py-3 pr-4 text-[#1C252E] font-medium">${(b.total_amount || 0).toFixed(2)}</td>
                                        <td className="py-3 pr-4 text-[#4A5568]">{b.emails_sent || 0}</td>
                                        <td className="py-3 pr-4 text-[#4A5568] text-xs">{b.generated_by}</td>
                                        <td className="py-3 text-[#4A5568] text-xs">{b.generated_at ? new Date(b.generated_at).toLocaleString() : ""}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TaxLettersPanel;
